import * as React from 'react';
import { Tag, Text, Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@components/atoms';
import type { TagProps } from '@components/atoms/tag/types';
import { useLayoutEffect } from '../../../hooks/use-isomorphic-layout-effect';
import { cn } from 'src/utils';

interface TagsProps {
	items: ReadonlyArray<TagProps>;
	overflowIndicatorWidth?: number;
	renderOverflow?: (items: ReadonlyArray<TagProps>) => React.ReactNode;
	truncate?: boolean;
}

const ELLIPSIS = '…';
const GAP_WIDTH = 4;
const ELLIPSIS_WIDTH = 12;

export const Tags = ({ items, overflowIndicatorWidth = ELLIPSIS_WIDTH, renderOverflow, truncate = false }: TagsProps) => {
	const containerRef = React.useRef<HTMLDivElement>(null);
	const tagRefs = React.useRef<(HTMLDivElement | null)[]>([]);
	const [calculatedMaxItems, setCalculatedMaxItems] = React.useState<number | null>(null);
	const [isInitializing, setIsInitializing] = React.useState(true);

	// Initialize tag refs array
	React.useEffect(() => {
		tagRefs.current = tagRefs.current.slice(0, items.length);
	}, [items.length]);

	// Calculate how many items can fit
	useLayoutEffect(() => {
		if (!truncate || !containerRef.current || items.length === 0) {
			return;
		}

		const calculateMaxItems = () => {
			if (!containerRef.current) return;

			setIsInitializing(true);
			const container = containerRef.current;
			const containerWidth = container.offsetWidth;

			if (containerWidth <= 0) {
				return;
			}

			let totalWidth = 0;
			let maxFit = 0;

			// Measure each rendered tag
			for (let i = 0; i < items.length; i++) {
				const tagElement = tagRefs.current[i];
				if (!tagElement) {
					// If we don't have a ref for this tag yet, break
					break;
				}

				const tagWidth = tagElement.offsetWidth;
				const nextVisibleWidth = totalWidth + tagWidth + (i > 0 ? GAP_WIDTH : 0);
				const hasOverflowItems = i < items.length - 1;
				const nextRequiredWidth = nextVisibleWidth + (hasOverflowItems ? overflowIndicatorWidth + GAP_WIDTH : 0);

				if (nextRequiredWidth <= containerWidth) {
					totalWidth = nextVisibleWidth;
					maxFit = i + 1;
				} else {
					break;
				}
			}

			setCalculatedMaxItems(maxFit);
			setIsInitializing(false);
		};

		// Use requestAnimationFrame to ensure DOM is ready
		const rafId = requestAnimationFrame(() => {
			calculateMaxItems();
		});

		// Recalculate on resize
		const resizeObserver = new ResizeObserver(() => {
			requestAnimationFrame(calculateMaxItems);
		});
		resizeObserver.observe(containerRef.current);

		return () => {
			cancelAnimationFrame(rafId);
			resizeObserver.disconnect();
		};
	}, [truncate, items, overflowIndicatorWidth]);

	const shouldUseTruncation = truncate && calculatedMaxItems !== null;
	const visibleItemCount = shouldUseTruncation ? calculatedMaxItems : items.length;
	const tooltipItems = shouldUseTruncation ? items.slice(visibleItemCount) : [];

	return (
		<div ref={containerRef} className={cn(isInitializing && 'invisible', 'relative flex flex-nowrap items-start gap-1 overflow-hidden')}>
			{items.map((item, index) => (
				<Tag
					truncate={truncate}
					key={index}
					ref={el => {
						tagRefs.current[index] = el;
					}}
					{...item}
					className={cn(item.className, index >= visibleItemCount && 'pointer-events-none invisible absolute')}
				/>
			))}
			{tooltipItems.length > 0 && (renderOverflow ? renderOverflow(tooltipItems) : <TagsTooltip items={tooltipItems} />)}
		</div>
	);
};

const TagsTooltip = ({ items }: { items: ReadonlyArray<TagProps> }) => {
	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger className="w-2">
					<Text size="md" color="darkGray" className="underline">
						{ELLIPSIS}
					</Text>
				</TooltipTrigger>
				<TooltipContent>
					<div className="flex flex-col flex-nowrap items-center gap-1">
						{items.map((item, index) => (
							<Tag key={index} {...item} />
						))}
					</div>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
};
